import { ReactNode } from "react";

import useAssistantPanelSendPrompt from "components/AssistantPanel/hooks/useSendPrompt";
import useAssistantPanelAnalytics from "components/AssistantPanel/analytics/useAnalytics";
import usePhaseSummary from "components/RunSummarization/hooks/usePhaseSummary";
import { buildPhaseSummaryPrompt } from "components/RunSummarization/prompts";
import RunSummarizationTrigger from "components/RunSummarization/RunSummarizationTrigger";
import Box from "ds/components/Box";
import Tree, { TreeBranch, TreeBranchLink, TreeItem } from "ds/components/Tree";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import { RunState } from "types/generated";

import ConciseLoggingBanner from "../ConciseLoggingBanner";
import RunLogs from "../RunLogs";
import RunPhaseLogsHooks from "./Hooks";
import styles from "./styles.module.css";

type RunPhaseLogsProps = {
  until: number | null;
  stateVersion: number | null;
  state: RunState;
  beforeHooks?: string[] | undefined;
  afterHooks?: string[] | undefined;
  children?: ReactNode;
  runId: string;
  stackId: string;
  isModuleRun: boolean;
};

const containersNotSupported = () => {
  // is safari
  if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) return true;
  // firefox before version 133.0
  const version = navigator.userAgent.match(/firefox\/(\d+)/i)?.[1];
  return version && parseInt(version) < 133;
};

const RunPhaseLogs = ({
  beforeHooks,
  afterHooks,
  children,
  isModuleRun,
  ...rest
}: RunPhaseLogsProps) => {
  const { openPrompt, isChatOpen } = useAssistantPanelSendPrompt();
  const trackSegmentAnalyticsEvent = useAnalytics();
  const { trackConversationOpened } = useAssistantPanelAnalytics();
  const stateVersion = rest.stateVersion ?? 0;

  const { spaceliftIntelligenceTermsAccepted, canTriggerSummary, showSummarizerTrigger } =
    usePhaseSummary();

  const logs = (
    <Box fullWidth direction="column" gap="large">
      <ConciseLoggingBanner state={rest.state} />

      <Box
        className={styles.logsContainer}
        direction={containersNotSupported() ? "column" : "row"}
        gap="x-large"
        fullWidth
      >
        <RunLogs isModuleRun={isModuleRun} {...rest} />
      </Box>
    </Box>
  );

  const disableAiTrigger = !canTriggerSummary.result || !spaceliftIntelligenceTermsAccepted;

  const generateTooltipMessage = () => {
    if (!spaceliftIntelligenceTermsAccepted) {
      return "An account admin must accept the terms and conditions before using Infra Assistant";
    }

    if (!canTriggerSummary.result && canTriggerSummary.message) {
      return canTriggerSummary.message;
    }

    return (
      <Box direction="column" gap="small">
        <Typography tag="p">Ask Infra Assistant</Typography>
        <Typography tag="p">
          Infra Assistant <b>quickly</b> summarizes the <b>current phase</b>, including execution
          errors.
        </Typography>
      </Box>
    );
  };

  const handleAssistantTrigger = () => {
    trackSegmentAnalyticsEvent("Run Logs - AI Phase Summarization Clicked", {
      stackId: rest.stackId,
      runId: rest.runId,
      stateVersion,
      via: "assistant",
    });

    trackConversationOpened("phase_summarization");

    const prompt = buildPhaseSummaryPrompt({
      stackId: rest.stackId,
      runId: rest.runId,
      state: rest.state,
    });

    openPrompt(prompt, { newChat: !isChatOpen });
  };

  const summarizationTrigger = (
    <>
      {showSummarizerTrigger && (
        <RunSummarizationTrigger
          onPress={handleAssistantTrigger}
          disabled={disableAiTrigger}
          tooltipText={generateTooltipMessage()}
        >
          Summarize
        </RunSummarizationTrigger>
      )}
    </>
  );

  if (!beforeHooks?.length && !afterHooks?.length) {
    return (
      <>
        {logs}
        <Box gap="medium" align="start">
          {children}

          {summarizationTrigger}
        </Box>
      </>
    );
  }

  return (
    <Tree padding="0 0 0 large">
      <RunPhaseLogsHooks type="before" hooks={beforeHooks} />

      <TreeBranch align="start">
        <TreeItem align="start" direction="column" gap="medium" autoHeight>
          <TreeBranchLink>{logs}</TreeBranchLink>
          <Box gap="medium" align="start">
            {children}

            {summarizationTrigger}
          </Box>
        </TreeItem>
      </TreeBranch>

      <RunPhaseLogsHooks type="after" hooks={afterHooks} />
    </Tree>
  );
};

export default RunPhaseLogs;
